import React, { useState, useEffect, useCallback } from 'react';
import {
  Gift, Settings, Users, DollarSign, TrendingUp, CheckCircle,
  XCircle, Clock, Upload, AlertTriangle, ChevronRight, Percent,
  Layers, Shield, Calendar, FileText, Eye, Check, X, RefreshCw,
  Building, Target, Award, PieChart
} from 'lucide-react';
import { COLORS } from '../../../constants/dashboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MatchingGiftView = ({ userRole = 'business_admin' }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [settings, setSettings] = useState(null);
  const [companyStats, setCompanyStats] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState({ pending: [], vesting: [], recently_processed: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, statsRes, approvalsRes] = await Promise.all([
        fetch(`${API_URL}/api/matching/settings`, { credentials: 'include' }),
        fetch(`${API_URL}/api/matching/company-stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/matching/pending-approvals`, { credentials: 'include' })
      ]);

      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(data);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setCompanyStats(data);
      }
      if (approvalsRes.ok) {
        const data = await approvalsRes.json();
        setPendingApprovals(data);
      }
    } catch (error) {
      console.error('Error fetching matching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateSettings = async (updates) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/matching/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        setSettings(prev => ({ ...prev, ...updates }));
        setMessage({ type: 'success', text: 'Settings updated successfully!' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleApproval = async (matchRequestId, action, reason = '') => {
    try {
      const response = await fetch(`${API_URL}/api/matching/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ match_request_id: matchRequestId, action, reason })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `Match request ${action}d successfully!` });
        fetchData();
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to process approval' });
    }
  };

  const processVesting = async () => {
    try {
      const response = await fetch(`${API_URL}/api/matching/process-vesting`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: `Processed ${data.processed_count} vested matches` });
        fetchData();
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to process vesting' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: COLORS.primary }} />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: PieChart },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'approvals', label: 'Approvals', icon: CheckCircle, badge: pendingApprovals.pending_count },
    { id: 'vesting', label: 'Vesting', icon: Clock, badge: pendingApprovals.vesting_count }
  ];

  return (
    <div className="p-6" style={{ background: COLORS.background }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Matching Gift Engine</h1>
          <p className="text-sm text-gray-500">Configure and manage employee donation matching</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${settings?.matching_enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {settings?.matching_enabled ? 'Matching Active' : 'Matching Disabled'}
          </span>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.badge > 0 && (
              <span className="px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && companyStats && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-5 border shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${COLORS.primary}15` }}>
                  <DollarSign className="w-5 h-5" style={{ color: COLORS.primary }} />
                </div>
                <span className="text-sm text-gray-500">Employee Donations</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">${companyStats.total_employee_donations?.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">{companyStats.year} Year to Date</p>
            </div>

            <div className="bg-white rounded-xl p-5 border shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-50">
                  <Gift className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm text-gray-500">Company Match</span>
              </div>
              <p className="text-2xl font-bold text-green-600">${companyStats.total_company_match?.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">Total matched this year</p>
            </div>

            <div className="bg-white rounded-xl p-5 border shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-50">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <span className="text-sm text-gray-500">Participating</span>
              </div>
              <p className="text-2xl font-bold text-purple-600">{companyStats.employees_participating}</p>
              <p className="text-xs text-gray-400 mt-1">Employees with matches</p>
            </div>

            <div className="bg-white rounded-xl p-5 border shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-orange-50">
                  <TrendingUp className="w-5 h-5 text-orange-600" />
                </div>
                <span className="text-sm text-gray-500">Avg. Donation</span>
              </div>
              <p className="text-2xl font-bold text-orange-600">${companyStats.average_donation?.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">Per donation</p>
            </div>
          </div>

          {/* Pending Actions */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-5 border shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange-500" />
                Pending Actions
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-600" />
                    <span className="text-sm text-gray-700">Pending Approvals</span>
                  </div>
                  <span className="font-bold text-orange-600">{companyStats.pending_approvals}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-gray-700">In Vesting Period</span>
                  </div>
                  <span className="font-bold text-blue-600">{companyStats.vesting_count}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Upload className="w-4 h-4 text-purple-500" />
                External Donations
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Pending Requests</span>
                  <span className="font-semibold text-gray-900">{companyStats.external_donations?.pending_count || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Approved Amount</span>
                  <span className="font-semibold text-green-600">${companyStats.external_donations?.approved_amount?.toLocaleString() || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Requests</span>
                  <span className="font-semibold text-gray-900">{companyStats.external_donations?.total_requests || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Settings Summary */}
          <div className="bg-white rounded-xl p-5 border shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4">Current Settings</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Annual Budget/Employee</p>
                <p className="text-lg font-bold" style={{ color: COLORS.primary }}>
                  ${settings?.annual_match_budget_per_employee?.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Default Match Ratio</p>
                <p className="text-lg font-bold" style={{ color: COLORS.primary }}>
                  {settings?.default_match_ratio}:1
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Tiered Matching</p>
                <p className="text-lg font-bold" style={{ color: settings?.tiered_matching_enabled ? COLORS.primary : '#9CA3AF' }}>
                  {settings?.tiered_matching_enabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">External Donations</p>
                <p className="text-lg font-bold" style={{ color: settings?.external_donations_enabled ? COLORS.primary : '#9CA3AF' }}>
                  {settings?.external_donations_enabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && settings && (
        <div className="space-y-6">
          {/* Core Settings */}
          <div className="bg-white rounded-xl p-6 border shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5" style={{ color: COLORS.primary }} />
              Core Matching Settings
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Matching Enabled</label>
                <button
                  onClick={() => updateSettings({ matching_enabled: !settings.matching_enabled })}
                  className={`w-full p-3 rounded-lg border-2 flex items-center justify-between ${
                    settings.matching_enabled ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <span className={settings.matching_enabled ? 'text-green-700' : 'text-gray-600'}>
                    {settings.matching_enabled ? 'Matching is Active' : 'Matching is Disabled'}
                  </span>
                  <div className={`w-12 h-6 rounded-full p-1 transition-all ${settings.matching_enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                    <div className={`w-4 h-4 bg-white rounded-full transition-all ${settings.matching_enabled ? 'translate-x-6' : ''}`} />
                  </div>
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Annual Budget per Employee</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    value={settings.annual_match_budget_per_employee}
                    onChange={(e) => updateSettings({ annual_match_budget_per_employee: parseFloat(e.target.value) || 0 })}
                    className="w-full pl-8 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Default Match Ratio</label>
                <select
                  value={settings.default_match_ratio}
                  onChange={(e) => updateSettings({ default_match_ratio: parseFloat(e.target.value) })}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value={0.5}>0.5:1 (50% match)</option>
                  <option value={1}>1:1 (100% match)</option>
                  <option value={1.5}>1.5:1 (150% match)</option>
                  <option value={2}>2:1 (200% match)</option>
                  <option value={3}>3:1 (300% match)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Auto-Approve Limit</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    value={settings.auto_approve_limit}
                    onChange={(e) => updateSettings({ auto_approve_limit: parseFloat(e.target.value) || 0 })}
                    className="w-full pl-8 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Matches under this amount are auto-approved</p>
              </div>
            </div>
          </div>

          {/* Tiered Matching */}
          <div className="bg-white rounded-xl p-6 border shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Layers className="w-5 h-5" style={{ color: COLORS.secondary }} />
                Tiered Matching
              </h3>
              <button
                onClick={() => updateSettings({ tiered_matching_enabled: !settings.tiered_matching_enabled })}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  settings.tiered_matching_enabled ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {settings.tiered_matching_enabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>
            {settings.tiered_matching_enabled && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Configure different match ratios for different donation tiers:</p>
                <div className="grid grid-cols-3 gap-3">
                  {(settings.match_tiers || [
                    { up_to: 500, ratio: 2.0 },
                    { up_to: 1000, ratio: 1.0 },
                    { up_to: 2000, ratio: 0.5 }
                  ]).map((tier, idx) => (
                    <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Tier {idx + 1}</p>
                      <p className="font-semibold">Up to ${tier.up_to.toLocaleString()}</p>
                      <p className="text-sm" style={{ color: COLORS.primary }}>{tier.ratio}:1 match</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Employment & Eligibility */}
          <div className="bg-white rounded-xl p-6 border shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" style={{ color: COLORS.accent }} />
              Eligibility & Controls
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.employment_eligibility_enabled}
                    onChange={(e) => updateSettings({ employment_eligibility_enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <p className="font-medium text-gray-700">Employment Tenure Required</p>
                    <p className="text-xs text-gray-500">Employees must work X days before eligible</p>
                  </div>
                </label>
                {settings.employment_eligibility_enabled && (
                  <div className="mt-3 ml-8">
                    <input
                      type="number"
                      value={settings.min_employment_days}
                      onChange={(e) => updateSettings({ min_employment_days: parseInt(e.target.value) || 0 })}
                      className="w-24 p-2 border rounded-lg text-sm"
                    />
                    <span className="ml-2 text-sm text-gray-600">days minimum</span>
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.approval_workflow_enabled}
                    onChange={(e) => updateSettings({ approval_workflow_enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <p className="font-medium text-gray-700">Approval Workflow</p>
                    <p className="text-xs text-gray-500">Require admin approval for large matches</p>
                  </div>
                </label>
              </div>

              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.vesting_enabled}
                    onChange={(e) => updateSettings({ vesting_enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <p className="font-medium text-gray-700">Match Vesting Period</p>
                    <p className="text-xs text-gray-500">Delay match finalization</p>
                  </div>
                </label>
                {settings.vesting_enabled && (
                  <div className="mt-3 ml-8">
                    <input
                      type="number"
                      value={settings.vesting_period_days}
                      onChange={(e) => updateSettings({ vesting_period_days: parseInt(e.target.value) || 30 })}
                      className="w-24 p-2 border rounded-lg text-sm"
                    />
                    <span className="ml-2 text-sm text-gray-600">days vesting</span>
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.external_donations_enabled}
                    onChange={(e) => updateSettings({ external_donations_enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <p className="font-medium text-gray-700">External Donation Matching</p>
                    <p className="text-xs text-gray-500">Allow receipt uploads for outside donations</p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Charity Eligibility */}
          <div className="bg-white rounded-xl p-6 border shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Building className="w-5 h-5" style={{ color: COLORS.primary }} />
                Charity Eligibility Rules
              </h3>
              <button
                onClick={() => updateSettings({ charity_eligibility_enabled: !settings.charity_eligibility_enabled })}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  settings.charity_eligibility_enabled ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {settings.charity_eligibility_enabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>
            {settings.charity_eligibility_enabled && (
              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.only_verified_charities}
                      onChange={(e) => updateSettings({ only_verified_charities: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Only match verified 501(c)(3) organizations</span>
                  </label>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Excluded Categories</p>
                  <div className="flex flex-wrap gap-2">
                    {['Religious', 'Political', 'International'].map(cat => (
                      <button
                        key={cat}
                        onClick={() => {
                          const excluded = settings.excluded_charity_categories || [];
                          const updated = excluded.includes(cat)
                            ? excluded.filter(c => c !== cat)
                            : [...excluded, cat];
                          updateSettings({ excluded_charity_categories: updated });
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                          (settings.excluded_charity_categories || []).includes(cat)
                            ? 'bg-red-100 border-red-300 text-red-700'
                            : 'bg-gray-50 border-gray-200 text-gray-600'
                        }`}
                      >
                        {(settings.excluded_charity_categories || []).includes(cat) ? '✕ ' : ''}{cat}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Approvals Tab */}
      {activeTab === 'approvals' && (
        <div className="space-y-4">
          {pendingApprovals.pending.length === 0 ? (
            <div className="bg-white rounded-xl p-8 border shadow-sm text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
              <p className="text-gray-600">No pending approvals</p>
            </div>
          ) : (
            pendingApprovals.pending.map(request => (
              <div key={request.match_request_id} className="bg-white rounded-xl p-5 border shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                      <Upload className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{request.employee_name}</p>
                      <p className="text-sm text-gray-500">{request.employee_email}</p>
                      <div className="mt-2 flex items-center gap-4">
                        <span className="text-sm"><strong>Charity:</strong> {request.charity_name}</span>
                        <span className="text-sm"><strong>Donation:</strong> ${request.donation_amount}</span>
                        <span className="text-sm text-green-600"><strong>Match:</strong> ${request.match_amount}</span>
                      </div>
                      {request.source_type === 'external' && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full">External Donation</span>
                          {request.receipt_url && (
                            <a href={request.receipt_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                              <Eye className="w-3 h-3" /> View Receipt
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApproval(request.match_request_id, 'approve')}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 flex items-center gap-1"
                    >
                      <Check className="w-4 h-4" /> Approve
                    </button>
                    <button
                      onClick={() => handleApproval(request.match_request_id, 'reject', 'Does not meet criteria')}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 flex items-center gap-1"
                    >
                      <X className="w-4 h-4" /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Vesting Tab */}
      {activeTab === 'vesting' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={processVesting}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Process Vested Matches
            </button>
          </div>
          
          {pendingApprovals.vesting.length === 0 ? (
            <div className="bg-white rounded-xl p-8 border shadow-sm text-center">
              <Clock className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600">No matches in vesting period</p>
            </div>
          ) : (
            pendingApprovals.vesting.map(request => (
              <div key={request.match_request_id} className="bg-white rounded-xl p-5 border shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{request.employee_name}</p>
                      <p className="text-sm text-gray-500">${request.match_amount} match for {request.charity_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Vesting ends</p>
                    <p className="font-semibold text-blue-600">
                      {new Date(request.vesting_ends_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MatchingGiftView;
